/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Book a MODEL. First checks which rental companies offer the model for the dates:
 *  - 0 companies → nothing available;
 *  - 1 company   → straight to booking (auto-selects an available car of that company);
 *  - 2+ companies → an intermediate company picker, then booking scoped to the chosen company.
 * On a tenant storefront the scope is that owner; from the main domain it spans all companies
 * but is restricted to the selected city (?city=).
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Car, ModelCompany } from '../types';
import api from '../services/api';
import BookingView from '../components/booking/BookingView';
import CompanyPicker from '../components/booking/CompanyPicker';

function isoDate(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 864e5).toISOString();
}

export default function ModelBookingPage() {
  const { t } = useTranslation();
  const { make, model } = useParams<{ make: string; model: string }>();
  const [params] = useSearchParams();
  const city = params.get('city') || undefined;

  const mk = make ? decodeURIComponent(make) : undefined;
  const md = model ? decodeURIComponent(model) : '';

  const [companies, setCompanies] = useState<ModelCompany[] | null>(null);
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const [car, setCar] = useState<Car | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Which companies offer this model for the (default) dates.
  useEffect(() => {
    if (!md) return;
    setLoading(true);
    setCompanies(null);
    setOwnerId(undefined);
    api
      .getModelCompanies({ make: mk, model: md, city, dateFrom: isoDate(1), dateTo: isoDate(8) })
      .then((list) => {
        setCompanies(list);
        if (list.length <= 1) setOwnerId(list[0]?.ownerId); // 0 or 1 company → skip the picker
      })
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mk, md, city]);

  // Once a company is settled (auto for ≤1, or picked), resolve a car of the model for it.
  useEffect(() => {
    if (!md || !companies || companies.length === 0) return;
    if (companies.length > 1 && !ownerId) return; // waiting on the picker
    api
      .resolveModelCar({ make: mk, model: md, city, ownerId, dateFrom: isoDate(1), dateTo: isoDate(8) })
      .then(({ car, availableCount }) => { setCar(car); setCount(availableCount); })
      .catch(() => { setCar(null); setCount(0); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mk, md, city, ownerId, companies]);

  if (loading)
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">{t('detail.loading')}</div>;

  if (!companies || companies.length === 0)
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-4">
        <p className="text-gray-700">{t('booking.noModelAvailable', { model: md })}</p>
        <Link to="/" className="mt-3 underline text-blue-600">{t('detail.backToSearch')}</Link>
      </div>
    );

  // More than one company and none chosen yet → show the picker.
  if (companies.length > 1 && !ownerId)
    return <CompanyPicker model={md} companies={companies} onSelect={setOwnerId} />;

  if (!car)
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">{t('detail.loading')}</div>;

  return (
    <BookingView
      car={car}
      availableCount={count}
      modelMake={mk}
      modelModel={md}
      modelCity={city}
      modelOwnerId={ownerId}
    />
  );
}
